import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '../../components/Common/Box';
import CompanyLogo from '../../components/Common/CompanyLogo';
import Disclaimer from '../../components/Common/Disclaimer';
import AllocationChart from './components/AllocationChart';
import {
  AlertesComportementales,
  EvolutionCard,
  KpiCard,
  PeriodesCard,
  note,
} from './components/PortfolioWidgets';
import { PortfolioAnalysisData } from './usePortfolioAnalysis';
import {
  cours,
  couleur,
  dateLongue,
  entier,
  fcfa,
  fcfaSigne,
  pct,
  pctSigne,
  ratio,
} from './portfolioFormat';
import { ArrowDownUp } from 'lucide-react';

interface MobilePortfolioAnalysisProps {
  data: PortfolioAnalysisData;
}

type SortOption = 'weight_desc' | 'symbol_asc';

const MobilePortfolioAnalysis: React.FC<MobilePortfolioAnalysisProps> = ({ data }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'summary' | 'charts' | 'assets'>('summary');
  const [assetSort, setAssetSort] = useState<SortOption>('weight_desc');

  // Sorted assets memoization
  const sortedAssets = useMemo(() => {
    const assets = [...data.performance_by_asset];
    if (assetSort === 'weight_desc') {
      return assets.sort((a, b) => b.weight_pct - a.weight_pct);
    } else if (assetSort === 'symbol_asc') {
      return assets.sort((a, b) => a.symbol.localeCompare(b.symbol));
    }
    return assets;
  }, [data.performance_by_asset, assetSort]);

  return (
    <div style={{ paddingBottom: '20px' }}>
      {/* 1. Solde global toujours visible */}
      <div
        style={{
          padding: '24px 20px',
          background: 'var(--bg-box)',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <h2
          style={{
            margin: '0 0 8px 0',
            fontSize: '15px',
            color: 'var(--text-muted)',
            fontWeight: '500',
          }}
        >
          Valeur des titres
        </h2>
        <div
          style={{
            fontSize: '30px',
            fontWeight: '800',
            color: 'var(--text-heading)',
            marginBottom: '4px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fcfa(data.total_value)}
        </div>
        <div
          style={{ fontSize: '15px', fontWeight: '600', color: couleur(data.total_profit_loss) }}
        >
          {fcfaSigne(data.total_profit_loss)}
          <span style={{ fontSize: '13px', marginLeft: '6px' }}>
            ({pctSigne(data.total_profit_loss_pct)})
          </span>
        </div>
        <div style={{ ...note, marginTop: '6px', textAlign: 'center' }}>
          Plus-value latente avant frais · liquidités non comprises · cours du{' '}
          {dateLongue(data.cours_au)}
        </div>
      </div>

      {/* 2. Navigation Mobile (Segmented Control) */}
      <div
        style={{
          padding: '16px 20px',
          position: 'sticky',
          top: '0',
          background: 'var(--bg-main)',
          zIndex: 10,
          borderBottom: '1px solid var(--border-color)',
        }}
      >
        <div
          style={{
            display: 'flex',
            background: 'var(--bg-input)',
            borderRadius: '12px',
            padding: '4px',
          }}
        >
          <button
            onClick={() => setActiveTab('summary')}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              background: activeTab === 'summary' ? '#2563eb' : 'transparent',
              color: activeTab === 'summary' ? '#ffffff' : 'var(--text-muted)',
              border: 'none',
              boxShadow: activeTab === 'summary' ? '0 2px 4px rgba(37, 99, 235, 0.2)' : 'none',
              transition: 'all 0.2s',
              touchAction: 'manipulation',
            }}
          >
            Résumé
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              background: activeTab === 'charts' ? '#2563eb' : 'transparent',
              color: activeTab === 'charts' ? '#ffffff' : 'var(--text-muted)',
              border: 'none',
              boxShadow: activeTab === 'charts' ? '0 2px 4px rgba(37, 99, 235, 0.2)' : 'none',
              transition: 'all 0.2s',
              touchAction: 'manipulation',
            }}
          >
            Graphiques
          </button>
          <button
            onClick={() => setActiveTab('assets')}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              background: activeTab === 'assets' ? '#2563eb' : 'transparent',
              color: activeTab === 'assets' ? '#ffffff' : 'var(--text-muted)',
              border: 'none',
              boxShadow: activeTab === 'assets' ? '0 2px 4px rgba(37, 99, 235, 0.2)' : 'none',
              transition: 'all 0.2s',
              touchAction: 'manipulation',
            }}
          >
            Actifs ({data.performance_by_asset.length})
          </button>
        </div>
      </div>

      {/* 3. Contenu des Onglets */}
      <div style={{ padding: '20px' }}>
        {/* --- ONGLET: RÉSUMÉ --- */}
        {activeTab === 'summary' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <PeriodesCard data={data} compact />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <KpiCard
                compact
                libelle='Dividendes annuels'
                valeur={fcfa(data.expected_annual_dividends)}
                detail={`Bruts, avant IRVM · ${pct(data.portfolio_yield_pct, 2)}`}
              />
              <KpiCard
                compact
                libelle='Frais de courtage'
                valeur={fcfa(data.total_fees)}
                couleurValeur='#ef4444'
                detail='Cumul, non déduits'
              />
              <KpiCard
                compact
                libelle='Ratio de Sharpe'
                valeur={ratio(data.sharpe_like_ratio)}
                detail={
                  data.sharpe_like_ratio !== null
                    ? `Hors apports, ${data.history_days_available} séances`
                    : 'Après 20 séances'
                }
              />
              <KpiCard
                compact
                libelle='PER du portefeuille'
                valeur={ratio(data.weighted_per)}
                detail='Moyenne harmonique'
              />
            </div>

            <AlertesComportementales />
          </div>
        )}

        {/* --- ONGLET: GRAPHIQUES --- */}
        {activeTab === 'charts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <EvolutionCard data={data} hauteur={250} />
            <AllocationChart title='Allocation sectorielle' data={data.sector_allocation} />
            <AllocationChart title='Allocation géographique' data={data.country_allocation} />
          </div>
        )}

        {/* --- ONGLET: ACTIFS --- */}
        {activeTab === 'assets' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Contrôle de tri personnalisé */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-heading)' }}>
                Vos Titres
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ArrowDownUp size={14} color='var(--text-muted)' />
                <select
                  value={assetSort}
                  onChange={(e) => setAssetSort(e.target.value as SortOption)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-main)',
                    fontSize: '13px',
                    fontWeight: '500',
                    outline: 'none',
                    padding: '4px',
                  }}
                >
                  <option value='weight_desc'>Trier par poids</option>
                  <option value='symbol_asc'>Trier par symbole</option>
                </select>
              </div>
            </div>

            {sortedAssets.map((item) => (
              <Box key={item.symbol}>
                <div style={{ padding: '16px' }}>
                  {/* En-tête de carte */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                        minWidth: 0,
                      }}
                    >
                      <CompanyLogo symbol={item.symbol} size={36} />
                      <div style={{ minWidth: 0 }}>
                        <button
                          type='button'
                          style={{
                            font: 'inherit', // avant fontWeight : le raccourci `font` le réinitialise
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: '#2563eb',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            marginBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                          onClick={() => navigate(`/data?symbol=${item.symbol}`)}
                        >
                          {item.symbol}
                          {item.data_unavailable && (
                            <span
                              title={item.data_unavailable_reason ?? 'Donnée indisponible'}
                              style={{
                                fontSize: '10px',
                                fontWeight: 'bold',
                                color: '#f59e0b',
                                border: '1px solid #f59e0b',
                                borderRadius: '4px',
                                padding: '1px 4px',
                              }}
                            >
                              ⚠ Cours indisponible
                            </span>
                          )}
                        </button>
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--text-muted)',
                            marginBottom: '2px',
                          }}
                        >
                          {item.stock_name}
                        </div>
                        <div
                          style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: 600 }}
                        >
                          {fcfa(item.current_value)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {pct(item.weight_pct)} du portefeuille · {entier(item.quantity)} titres
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: couleur(item.perf_absolute),
                        }}
                      >
                        {Math.round(item.perf_absolute) > 0 ? '+' : ''}
                        {entier(item.perf_absolute)} F
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: couleur(item.perf_pct),
                        }}
                      >
                        {pctSigne(item.perf_pct)}
                      </div>
                    </div>
                  </div>

                  {/* Détails secondaires compacts */}
                  <div
                    style={{
                      background: 'var(--bg-hover)',
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '12px',
                    }}
                  >
                    {[
                      { libelle: 'PRU', valeur: cours(item.pru) },
                      { libelle: 'Cours', valeur: cours(item.current_price) },
                      { libelle: 'Volatilité 30 j', valeur: pct(item.volatility_pct) },
                      {
                        libelle: 'Jour',
                        valeur: pctSigne(item.pl_day_pct),
                        c: couleur(item.pl_day_pct),
                      },
                      {
                        libelle: 'Mois',
                        valeur: pctSigne(item.pl_month_pct),
                        c: couleur(item.pl_month_pct),
                      },
                      {
                        libelle:
                          item.perf_ytd_base === 'ACHAT' ? 'Depuis achat*' : 'Depuis 1er janv.',
                        valeur: pctSigne(item.perf_ytd_pct),
                        c: couleur(item.perf_ytd_pct),
                      },
                      { libelle: 'Rendement brut', valeur: pct(item.rendement_brut_pct, 2) },
                      { libelle: 'Rendement net*', valeur: pct(item.rendement_net_pp_pct, 2) },
                      {
                        libelle: 'Dividende annuel',
                        valeur: entier(item.expected_annual_dividend),
                      },
                    ].map((d) => (
                      <div key={d.libelle}>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {d.libelle}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: d.c ?? 'var(--text-main)',
                          }}
                        >
                          {d.valeur}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Box>
            ))}
            <p style={{ ...note, margin: 0 }}>
              Plus-values avant frais de courtage. Depuis achat* : titre acheté en cours d'année,
              performance depuis l'achat. Rendement net* : après IRVM des personnes physiques du
              pays de l'émetteur. Dividende annuel brut, en F CFA.
            </p>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          <Disclaimer type='investment' compact />
        </div>
      </div>
    </div>
  );
};

export default MobilePortfolioAnalysis;
